import * as React from 'react';
import { observer } from 'mobx-react';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { ChoiceGroup, IChoiceGroupOption } from 'office-ui-fabric-react/lib/ChoiceGroup';
import { connectedAccountsStore } from '../store/store';
import { ConnectionType } from '../store/schema/ConnectionType';
import { updateAdvancedConnectionType } from '../actions/connectAdvanced/updateAdvancedConnectionType';
import { updateAdvancedIsConnecting } from '../actions/connectAdvanced/updateAdvancedIsConnecting';
import { updateAdvancedImapServer } from '../actions/connectAdvanced/updateAdvancedImapServer';
import { updateAdvancedPopServer } from '../actions/connectAdvanced/updateAdvancedPopServer';
import { updateAdvancedIncomingPort } from '../actions/connectAdvanced/updateAdvancedIncomingPort';
import { updateAdvancedLeaveCopy } from '../actions/connectAdvanced/updateAdvancedLeaveCopy';
import { updateAdvancedRemoteSend } from '../actions/connectAdvanced/updateAdvancedRemoteSend';
import { updateAdvancedOutgoingServer } from '../actions/connectAdvanced/updateAdvancedOutgoingServer';
import { updateAdvancedOutgoingPort } from '../actions/connectAdvanced/updateAdvancedOutgoingPort';
import { updateAdvancedUseAltUsernamePassword } from '../actions/connectAdvanced/updateAdvancedUseAltUsernamePassword';
import { updateAdvancedAltUsername } from '../actions/connectAdvanced/updateAdvancedAltUsername';
import { updateAdvancedAltPassword } from '../actions/connectAdvanced/updateAdvancedAltPassword';
import { updateAdvancedDisplayName } from '../actions/connectAdvanced/updateAdvancedDisplayName';
import { updateAdvancedEmailAddress } from '../actions/connectAdvanced/updateAdvancedEmailAddress';
import { updateAdvancedUserName } from '../actions/connectAdvanced/updateAdvancedUserName';
import { updateAdvancedPassword } from '../actions/connectAdvanced/updateAdvancedPassword';
import { updateAdvancedUseNewFolder } from '../actions/connectAdvanced/updateAdvancedUseNewFolder';
import { updateAdvancedIncomingSecurity } from '../actions/connectAdvanced/updateAdvancedIncomingSecurity';
import { updateAdvancedIncomingAuth } from '../actions/connectAdvanced/updateAdvancedIncomingAuth';
import { toggleConnectAdvancedWindow } from '../actions/connectAdvanced/toggleConnectAdvancedWindow';
import { updateAdvancedErrorMessage } from '../actions/connectAdvanced/updateAdvancedErrorMessage';
import { newSyncRequest } from '../services/newSyncRequest';
import { initConnectedAccountsFull } from '../orchestrators/initConnectedAccountsFull';
import strings from 'owa-strings/owa-options-strings';
import IncomingAccountInformation from './IncomingAccountInformation';
import IncomingServerInformation from './IncomingServerInformation';
import OutgoingServerInformation from './OutgoingServerInformation';
import { getConnectAdvancedSyncRequest } from '../selectors/getConnectAdvancedSyncRequest';
import { getErrorConnectAdvanced } from '../selectors/getErrorConnectAdvanced';

const styles = require('./ConnectAdvancedWindow.scss');

@observer
export default class ConnectAdvancedWindow extends React.Component<{}, {}> {
    private onClose = () => {
        toggleConnectAdvancedWindow({ connectAdvancedOpen: false });
    };

    private onConnect = () => {
        updateAdvancedIsConnecting(true);
        newSyncRequest(getConnectAdvancedSyncRequest()).then(response => {
            updateAdvancedIsConnecting(false);
            updateAdvancedErrorMessage(response.ErrorMessage);

            if (!response.ErrorMessage) {
                toggleConnectAdvancedWindow({ connectAdvancedOpen: false });
                initConnectedAccountsFull();
            }
        });
    };

    private onConnectionTypeChanged = (
        option: IChoiceGroupOption,
        evt?: React.FormEvent<HTMLElement | HTMLInputElement>
    ) => {
        if (option.key === 'ImapSmtp') {
            updateAdvancedConnectionType(ConnectionType.ImapSmtp);
        } else if (option.key === 'PopSmtp') {
            updateAdvancedConnectionType(ConnectionType.PopSmtp);
        } else if (option.key === 'SmtpOnly') {
            updateAdvancedConnectionType(ConnectionType.SmtpOnly);
        }
    };

    render() {
        let store = connectedAccountsStore();

        return (
            <Dialog
                hidden={!store.dialogWindowState.connectAdvancedOpen}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: strings.options_connected_advanced_title,
                }}
                modalProps={{
                    isBlocking: true,
                    containerClassName: styles.dialog,
                    onDismiss: this.onClose,
                }}>
                <IncomingAccountInformation
                    displayName={store.connectAdvancedState.displayName}
                    emailAddress={store.connectAdvancedState.emailAddress}
                    username={store.connectAdvancedState.username}
                    password={store.connectAdvancedState.password}
                    useNewFolder={store.connectAdvancedState.useNewFolder}
                    disableEmailAddress={false}
                    renderUserNameContent={true}
                    renderNewFolderContent={true}
                    isBasicAuth={true}
                    onUpdateDisplayName={updateAdvancedDisplayName}
                    onUpdateEmailAddress={updateAdvancedEmailAddress}
                    onUpdateUserName={updateAdvancedUserName}
                    onUpdatePassword={updateAdvancedPassword}
                    onUpdateUseNewFolder={updateAdvancedUseNewFolder}
                />
                <ChoiceGroup
                    options={[
                        {
                            key: 'ImapSmtp',
                            text: strings.options_connected_advanced_imapOption,
                            checked:
                                store.connectAdvancedState.connectionType ===
                                ConnectionType.ImapSmtp,
                        },
                        {
                            key: 'PopSmtp',
                            text: strings.options_connected_advanced_popOption,
                            checked:
                                store.connectAdvancedState.connectionType ===
                                ConnectionType.PopSmtp,
                        },
                        {
                            key: 'SmtpOnly',
                            text: strings.options_connected_advanced_sendOnlyOption,
                            checked:
                                store.connectAdvancedState.connectionType ===
                                ConnectionType.SmtpOnly,
                        },
                    ]}
                    className={styles.options}
                    required={true}
                    label={strings.options_connected_advanced_connectionTypeOptionLabel}
                    onChanged={this.onConnectionTypeChanged}
                />
                {store.connectAdvancedState.connectionType !== ConnectionType.SmtpOnly && (
                    <IncomingServerInformation
                        isBasicAuth={true}
                        connectionType={store.connectAdvancedState.connectionType}
                        renderRemoteSend={true}
                        remoteSend={store.connectAdvancedState.remoteSend}
                        incomingPort={store.connectAdvancedState.incomingPort}
                        leaveCopy={store.connectAdvancedState.leaveCopy}
                        leaveCopyDisabled={false}
                        basicAuthProps={{
                            connectionType: store.connectAdvancedState.connectionType,
                            incomingAuth: store.connectAdvancedState.incomingAuth,
                            incomingSecurity: store.connectAdvancedState.incomingSecurity,
                            onUpdateIncomingAuth: updateAdvancedIncomingAuth,
                            onUpdateIncomingSecurity: updateAdvancedIncomingSecurity,
                        }}
                        onUpdateIncomingServer={
                            store.connectAdvancedState.connectionType === ConnectionType.PopSmtp
                                ? updateAdvancedPopServer
                                : updateAdvancedImapServer
                        }
                        onUpdateIncomingPort={updateAdvancedIncomingPort}
                        onUpdateLeaveCopy={updateAdvancedLeaveCopy}
                        onUpdateRemoteSend={updateAdvancedRemoteSend}
                    />
                )}
                <OutgoingServerInformation
                    isBasicAuth={true}
                    outgoingPort={store.connectAdvancedState.outgoingPort}
                    onUpdateOutgoingServer={updateAdvancedOutgoingServer}
                    onUpdateOutgoingPort={updateAdvancedOutgoingPort}
                    isSendOnly={() =>
                        store.connectAdvancedState.connectionType === ConnectionType.SmtpOnly
                    }
                    isRemoteSend={() => store.connectAdvancedState.remoteSend}
                    smtpAccountInformationProps={{
                        useAltUserNamePassword: () =>
                            store.connectAdvancedState.useAltUsernamePassword,
                        onUpdateUseAltUserNamePassword: updateAdvancedUseAltUsernamePassword,
                        onUpdateSmtpUserName: updateAdvancedAltUsername,
                        onUpdateSmtpPassword: updateAdvancedAltPassword,
                    }}
                />
                <DialogFooter>
                    {store.connectAdvancedState.isConnecting && (
                        <Spinner size={SpinnerSize.large} className={styles.spinner} />
                    )}
                    {!store.connectAdvancedState.isConnecting && (
                        <div className={styles.error}>
                            {store.connectAdvancedState.errorMessage}
                        </div>
                    )}
                    <PrimaryButton
                        onClick={this.onConnect}
                        disabled={
                            getErrorConnectAdvanced() || store.connectAdvancedState.isConnecting
                        }>
                        {strings.options_connected_okButton}
                    </PrimaryButton>
                    <DefaultButton
                        onClick={this.onClose}
                        disabled={store.connectAdvancedState.isConnecting}>
                        {strings.options_connected_cancelButton}
                    </DefaultButton>
                </DialogFooter>
            </Dialog>
        );
    }
}
